import isNil from "lodash/isNil";
import type { DateTime } from "luxon";

import { DAILY_SPARKLINE_DAYS_BACK } from "@waterly/utils-isomorphic";

import type {
  MetricDataPointInput,
  MetricDataPointType,
  SiteDataEntriesCollectionType,
  SiteDataEntriesDayType,
  SiteDataEntriesHistoryCollectionType,
} from "../../api/generated/graphql";

import type { CurrentIntervalState } from "./currentInterval.slice";
import type { CurrentSiteState } from "./currentSite.slice";
import type { DataEntryDateState } from "./dataEntryDate.slice";
import type { WaterlyAppStateCreator } from "./waterlyAppStateCreator.type";

type DataPoints = Record<number, SiteDataEntriesCollectionType | undefined>;
type DataPointsHistory = Record<number, SiteDataEntriesHistoryCollectionType | undefined>;
type DataPointsInterval = Record<number, SiteDataEntriesDayType | undefined>;
type DirtyDataPoints = Record<number, MetricDataPointInput>;

export type DataPointsState = {
  dataPoints: DataPoints;
  dataPointsLoading: boolean;
  dataPointsHistory: DataPointsHistory;
  dataPointsHistoryLoading: boolean;
  dataPointsInterval: DataPointsInterval;
  dataPointsIntervalLoading: boolean;
  dirtyDataPoints: DirtyDataPoints;
};

// a change from the subscription or a save, the entry interval fields are set when the write landed on one
export type DataPointChange = {
  dataPoint: MetricDataPointType;
  entryIntervalDate?: string | null;
  entryIntervalTime?: string | null;
};

type DataPointsActions = {
  setDataPointsLoading: (dataPointsLoading: boolean) => void;
  setDataPointsIntervalLoading: (dataPointsIntervalLoading: boolean) => void;
  setDataPointsHistoryLoading: (dataPointsHistoryLoading: boolean) => void;
  mergeDataPoints: (entries: Record<number, SiteDataEntriesCollectionType>) => void;
  mergeDataPointsHistory: (dataPointsHistory: DataPointsHistory) => void;
  mergeDataPointsIntervalHistory: (dataPointsInterval: DataPointsInterval) => void;
  applyDataPointChange: (change: DataPointChange) => void;
};

const initialState: DataPointsState = {
  dataPoints: {},
  dataPointsLoading: false,
  dataPointsHistory: {},
  dataPointsHistoryLoading: false,
  dataPointsInterval: {},
  dataPointsIntervalLoading: false,
  dirtyDataPoints: {},
};

const effectiveNumericValueOf = (dataPoint: MetricDataPointType): number | null => {
  if (isNil(dataPoint.effectiveValue) || dataPoint.effectiveValue === "") {
    return null;
  }

  const parsed = Number(dataPoint.effectiveValue);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed;
};

// the dataEntriesDay data points hold the TsDataPoint shape while a change carries MetricDataPointType
const normalizeDayDataPoint = (dataPoint: MetricDataPointType) => {
  let normalizedId = "-1";
  if (!isNil(dataPoint.id)) {
    normalizedId = String(dataPoint.id);
  }

  return {
    ...dataPoint,
    id: normalizedId,
    intervalId: dataPoint.intervalId ?? null,
    effectiveNumericValue: effectiveNumericValueOf(dataPoint),
  };
};

type DayDataPointList = SiteDataEntriesDayType["dataPoints"];

const replaceDayDataPoint = (matching: DayDataPointList[number], dataPoint: MetricDataPointType) => {
  if (matching.dataPoint) {
    // the wall clock appliedAt the query set stays, only the value fields update
    matching.dataPoint = {
      ...matching.dataPoint,
      effectiveNumericValue: effectiveNumericValueOf(dataPoint),
      value: dataPoint.value ?? null,
      isNondetect: Boolean(dataPoint.isNondetect),
      comment: dataPoint.comment ?? null,
      appliedAt: dataPoint.appliedAt,
      reportDate: dataPoint.reportDate,
    };
    return;
  }

  matching.dataPoint = normalizeDayDataPoint(dataPoint) as unknown as NonNullable<
    DayDataPointList[number]["dataPoint"]
  >;
};

// a new entry interval from another client, insert in time order
const insertEntryDataPoint = (params: {
  dataPoints: DayDataPointList;
  dataPoint: MetricDataPointType;
  intervalId: number;
  metricId: number;
  entryIntervalDate: string;
  entryIntervalTime: string;
}) => {
  const { dataPoints, dataPoint, intervalId, metricId, entryIntervalDate, entryIntervalTime } = params;

  const orderKey = `${entryIntervalDate}T${entryIntervalTime}`;
  const insertIndex = dataPoints.findIndex((entry) => String(entry.appliedAt).slice(0, 19) > orderKey);

  const newEntryDataPoint = {
    __typename: "SiteDataEntriesDayDataPointType" as const,
    intervalId,
    time: entryIntervalTime,
    appliedAt: `${orderKey}.000Z`,
    reportDate: entryIntervalDate,
    dataPoint: normalizeDayDataPoint(dataPoint) as unknown as NonNullable<
      DayDataPointList[number]["dataPoint"]
    >,
    id: `entry-recent-${metricId}-${intervalId}`,
  };

  if (insertIndex === -1) {
    dataPoints.push(newEntryDataPoint);
  } else {
    dataPoints.splice(insertIndex, 0, newEntryDataPoint);
  }
};

const upsertDailyHistory = (history: MetricDataPointType[], dataPoint: MetricDataPointType) => {
  const existingIndex = history.findIndex((entry) => entry.reportDate === dataPoint.reportDate);

  if (existingIndex >= 0) {
    history[existingIndex] = dataPoint;
  } else {
    history.push(dataPoint);
  }
};

const applyToCurrent = (params: {
  dataPoints: DataPoints;
  dataPoint: MetricDataPointType;
  intervalId: number | null;
  selectedDateIso: string;
  selectedIntervalIds: number[];
}) => {
  const { dataPoints, dataPoint, intervalId, selectedDateIso, selectedIntervalIds } = params;

  if (dataPoint.reportDate !== selectedDateIso) {
    return;
  }

  // daily values always occupy the daily current, interval values only when selected
  let matchesSelection = intervalId === null;
  if (intervalId !== null) {
    matchesSelection = selectedIntervalIds.includes(intervalId);
  }
  if (!matchesSelection) {
    return;
  }

  const existing = dataPoints[dataPoint.metricId];
  if (!existing) {
    return;
  }

  dataPoints[dataPoint.metricId] = { ...existing, current: dataPoint };
};

const applyToDayDataPoints = (params: {
  intervalEntry: SiteDataEntriesDayType | undefined;
  change: DataPointChange;
  intervalId: number | null;
  isEntryFlexSite: boolean;
}) => {
  const { intervalEntry, change, intervalId, isEntryFlexSite } = params;
  if (!intervalEntry || intervalId === null) {
    return;
  }

  const { dataPoint } = change;
  const matching = intervalEntry.dataPoints.find(
    (entry) => entry.intervalId === intervalId && entry.reportDate === dataPoint.reportDate,
  );

  if (matching) {
    replaceDayDataPoint(matching, dataPoint);
    return;
  }

  if (isEntryFlexSite && change.entryIntervalDate && change.entryIntervalTime) {
    insertEntryDataPoint({
      dataPoints: intervalEntry.dataPoints,
      dataPoint,
      intervalId,
      metricId: dataPoint.metricId,
      entryIntervalDate: change.entryIntervalDate,
      entryIntervalTime: change.entryIntervalTime,
    });
  }
};

const applyToDailyHistory = (params: {
  historyEntry: SiteDataEntriesHistoryCollectionType | undefined;
  dataPoint: MetricDataPointType;
  intervalId: number | null;
  selectedDate: DateTime<true>;
}) => {
  const { historyEntry, dataPoint, intervalId, selectedDate } = params;
  if (!historyEntry || intervalId !== null) {
    return;
  }

  // the daily sparkline holds the prior days only, the viewed day renders from current
  const selectedDateIso = selectedDate.toISODate();
  const floorIso = selectedDate.minus({ days: DAILY_SPARKLINE_DAYS_BACK }).toISODate();
  if (dataPoint.reportDate < floorIso || dataPoint.reportDate >= selectedDateIso) {
    return;
  }

  upsertDailyHistory(historyEntry.history, dataPoint);
};

type DataPointsExternalState = DataEntryDateState &
  Pick<CurrentIntervalState, "currentInterval"> &
  Pick<CurrentSiteState, "currentSite">;

export const createDataPointsSlice: WaterlyAppStateCreator<
  DataPointsActions,
  DataPointsState,
  DataPointsExternalState
> = (set) => {
  return {
    ...initialState,

    setDataPointsLoading: (dataPointsLoading: boolean) =>
      set({ dataPointsLoading }, undefined, "dataPoints:setDataPointsLoading"),
    setDataPointsHistoryLoading: (dataPointsHistoryLoading: boolean) =>
      set({ dataPointsHistoryLoading }, undefined, "dataPoints:setDataPointsHistoryLoading"),
    setDataPointsIntervalLoading: (dataPointsIntervalLoading: boolean) =>
      set({ dataPointsIntervalLoading }, undefined, "dataPoints:setDataPointsIntervalLoading"),

    mergeDataPoints: (entries: Record<number, SiteDataEntriesCollectionType>) =>
      set(
        (state) => {
          for (const [id, value] of Object.entries(entries)) {
            state.dataPoints[+id] = value;
          }
        },
        false,
        "dataPoints:mergeDataPoints",
      ),

    mergeDataPointsHistory: (dataPointsHistory: DataPointsHistory) =>
      set(
        (state) => {
          for (const [id, value] of Object.entries(dataPointsHistory)) {
            state.dataPointsHistory[+id] = value;
          }
        },
        false,
        "dataPoints:mergeDataPointsHistory",
      ),

    mergeDataPointsIntervalHistory: (dataPointsInterval: DataPointsInterval) =>
      set(
        (state) => {
          for (const [id, value] of Object.entries(dataPointsInterval)) {
            state.dataPointsInterval[+id] = value;
          }
        },
        false,
        "dataPoints:mergeDataPointsIntervalHistory",
      ),
    // changes arrive pre filtered by the subscription window, this places them by exact key and drops the rest
    applyDataPointChange: (change: DataPointChange) =>
      set(
        (state) => {
          const { dataPoint } = change;
          if (!dataPoint.metricId) return;

          const intervalId = dataPoint.intervalId ?? null;

          applyToCurrent({
            dataPoints: state.dataPoints,
            dataPoint,
            intervalId,
            selectedDateIso: state.dataEntryDateDateTime.toISODate(),
            selectedIntervalIds: state.currentInterval?.ids ?? [],
          });

          applyToDayDataPoints({
            intervalEntry: state.dataPointsInterval[dataPoint.metricId],
            change,
            intervalId,
            isEntryFlexSite: Boolean(state.currentSite?.flex && state.currentSite?.isEntry),
          });

          applyToDailyHistory({
            historyEntry: state.dataPointsHistory[dataPoint.metricId],
            dataPoint,
            intervalId,
            selectedDate: state.dataEntryDateDateTime,
          });
        },
        false,
        "dataPoints:applyDataPointChange",
      ),
  };
};
